'use client';

import { useState, useEffect, useRef } from 'react';
import { useTranslations } from 'next-intl';
import { useCertificationComparisonContext } from '@/contexts/CertificationComparisonContext';
import { motion, AnimatePresence } from 'framer-motion';

// Colors for comparison certifications — chosen to contrast with each other and with cluster colors
export const COMPARISON_COLORS = ['#0d9488', '#7c3aed'] as const; // teal-600, violet-700
export const PRIMARY_COLOR = '#1e293b'; // slate-900 — used for the primary cert badge in compare mode

interface CertificationComparisonSelectorProps {
    className?: string;
    showSelected?: boolean;
    vertical?: boolean;
    textClassName?: string;
}

export function CertificationComparisonSelector({
    className = '',
    showSelected = true,
    vertical = false,
    textClassName = ''
}: CertificationComparisonSelectorProps) {
    const t = useTranslations('certification.comparison');
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    // Local color state for instant swatch feedback while dragging
    const [localColors, setLocalColors] = useState<Map<string, string>>(new Map());
    const colorTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

    const handleColorChange = (certId: string, color: string) => {
        // Update swatch immediately
        setLocalColors(prev => new Map(prev).set(certId, color));
        // Debounce propagation to context (avoids re-rendering charts on every drag pixel)
        const existing = colorTimers.current.get(certId);
        if (existing) clearTimeout(existing);
        colorTimers.current.set(certId, setTimeout(() => {
            setComparisonColor(certId, color);
            colorTimers.current.delete(certId);
        }, 120));
    };

    const {
        comparisonCerts,
        availableCertifications,
        loading,
        loadingAvailable,
        addComparison,
        removeComparison,
        toggleVisibility,
        setComparisonColor,
        fetchAvailableCertifications,
        canAddMore,
        hideSelectionControls,
        primaryCertName,
        canEditColors,
    } = useCertificationComparisonContext();

    // Fetch available certifications when dropdown opens
    useEffect(() => {
        if (isOpen && availableCertifications.length === 0) {
            fetchAvailableCertifications();
        }
    }, [isOpen, availableCertifications.length, fetchAvailableCertifications]);

    // Close dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Filter out already selected certifications from available list
    const selectableCerts = availableCertifications.filter(
        c => !comparisonCerts.some(cc => cc.id === c.id)
    );

    const handleSelect = async (certId: string) => {
        await addComparison(certId);
        if (comparisonCerts.length >= 1) {
            setIsOpen(false);
        }
    };

    return (
        <div className={`relative ${className}`} ref={dropdownRef}>
            {/* Selected Comparisons Display */}
            <div className={`flex gap-2 ${vertical ? 'flex-col items-stretch' : 'items-center flex-wrap'}`}>
                {/* Currently selected comparisons */}
                {/* Primary cert badge — only in read-only compare mode */}
                {hideSelectionControls && primaryCertName && (
                    <div
                        className="flex items-center gap-1.5 px-2 py-1 text-xs font-medium border border-slate-200 w-full"
                        style={{
                            background: 'linear-gradient(to right, rgba(34,197,94,0.18) 25%, rgba(59,130,246,0.18) 25% 50%, rgba(245,158,11,0.18) 50% 75%, rgba(249,115,22,0.18) 75%)',
                            color: '#1e293b',
                        }}
                    >
                        <span className={`break-words leading-tight flex-1 ${textClassName}`}>
                            {primaryCertName}
                        </span>
                    </div>
                )}

                {showSelected && comparisonCerts.map((cert, idx) => {
                    const color = localColors.get(cert.id) || cert.color || COMPARISON_COLORS[idx % COMPARISON_COLORS.length];
                    return hideSelectionControls ? (
                        // Read-only badge (compare page)
                        <div
                            key={cert.id}
                            className="flex items-center gap-1.5 px-2 py-1 text-xs font-medium border w-full text-slate-800"
                            style={{
                                backgroundColor: `${color}30`,
                                borderColor: color,
                            }}
                        >
                            <span className={`break-words leading-tight flex-1 ${textClassName}`}>
                                {cert.name}
                            </span>
                            {canEditColors && (
                                <label className="shrink-0 cursor-pointer" title="Change color" onClick={e => e.stopPropagation()}>
                                    <div className="w-3.5 h-3.5 rounded-full border border-slate-400/40" style={{ backgroundColor: color }} />
                                    <input
                                        type="color"
                                        value={color}
                                        onChange={e => handleColorChange(cert.id, e.target.value)}
                                        className="sr-only"
                                    />
                                </label>
                            )}
                        </div>
                    ) : (
                        <div
                            key={cert.id}
                            onClick={() => toggleVisibility(cert.id)}
                            className="flex items-center justify-between gap-1.5 px-2 py-1 text-xs font-medium border transition-all cursor-pointer group hover:brightness-95 w-full"
                            style={{
                                backgroundColor: cert.isVisible ? `${color}30` : '#f1f5f9',
                                borderColor: cert.isVisible ? color : '#e2e8f0',
                                color: cert.isVisible ? '#1e293b' : '#94a3b8',
                            }}
                            title={cert.isVisible ? t('hide') : t('show')}
                        >
                            {canEditColors && (
                                <label className="shrink-0 cursor-pointer mr-0.5" title="Change color" onClick={e => e.stopPropagation()}>
                                    <div className="w-3.5 h-3.5 rounded-full border border-slate-400/40" style={{ backgroundColor: color }} />
                                    <input
                                        type="color"
                                        value={color}
                                        onChange={e => handleColorChange(cert.id, e.target.value)}
                                        className="sr-only"
                                    />
                                </label>
                            )}
                            <span className={`break-words leading-tight flex-1 ${textClassName}`}>
                                {cert.name}
                            </span>
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    removeComparison(cert.id);
                                }}
                                className="w-4 h-4 flex-shrink-0 flex items-center justify-center hover:text-red-500 transition-colors cursor-pointer opacity-0 group-hover:opacity-100"
                                title={t('remove')}
                            >
                                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                    );
                })}

                {/* Add Comparison Button — hidden in read-only mode */}
                {!hideSelectionControls && canAddMore && (
                    <button
                        onClick={() => setIsOpen(!isOpen)}
                        disabled={loading}
                        className="flex items-center gap-1.5 px-2 py-1 text-xs font-medium border border-dashed border-slate-300 text-slate-500 hover:border-violet-400 hover:text-violet-600 hover:bg-violet-50 transition-colors disabled:opacity-50 cursor-pointer"
                    >
                        {loading ? (
                            <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                            </svg>
                        ) : (
                            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                        )}
                        <span>{t('addComparison')}</span>
                    </button>
                )}

                {/* Max reached indicator — hidden in read-only mode */}
                {!hideSelectionControls && !canAddMore && comparisonCerts.length >= 2 && (
                    <span className="text-xs text-slate-400 italic">
                        {t('maxReached')}
                    </span>
                )}
            </div>
            {/* Dropdown */}
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.15 }}
                        className="absolute top-full left-0 mt-2 w-72 max-h-60 overflow-y-auto bg-white shadow-xl border border-slate-200 z-50"
                    >
                        {loadingAvailable ? (
                            <div className="p-4 text-center text-slate-500 text-sm">
                                <svg className="w-5 h-5 animate-spin mx-auto mb-2" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                </svg>
                                {t('loading')}
                            </div>
                        ) : selectableCerts.length === 0 ? (
                            <div className="p-4 text-center text-slate-500 text-sm">
                                {t('noAvailable')}
                            </div>
                        ) : (
                            <ul className="py-1">
                                {selectableCerts.map((cert) => (
                                    <li key={cert.id}>
                                        <button
                                            onClick={() => handleSelect(cert.id)}
                                            className="w-full text-left px-3 py-2 hover:bg-violet-50 text-sm transition-colors"
                                        >
                                            <div className="font-medium text-black truncate">
                                                {cert.name || 'Untitled'}
                                            </div>
                                            <div className="text-xs text-black flex items-center gap-2">
                                                {cert.site_city && <span>{cert.site_city}</span>}
                                                <span className="px-1.5 py-0.5 bg-slate-100 text-black border border-slate-200">
                                                    {cert.status}
                                                </span>
                                            </div>
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
